import * as Sentry from "@sentry/browser";
import { Button } from "antd";
import React, { ErrorInfo } from "react";

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

const fallBackUiContainerStyle: React.CSSProperties = {
  height: "100vh",
  textAlign: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const containerStyle: React.CSSProperties = {
  textAlign: "center",
  padding: "40px",
  display: "inline-block",
  fontSize: "22px",
};

const headingStyle: React.CSSProperties = {
  fontFamily: "Titillium Web",
  fontWeight: "bolder",
  fontSize: "62px",
};

const paragraphStyle: React.CSSProperties = {
  fontSize: "18px",
  color: "black",
  marginTop: "-25px",
};

interface FallBackUIProps {
  refresh: () => void;
  error?: Error | null;
  errorInfo?: ErrorInfo | null;
}

export const FallBackUI = ({
  refresh,
  error,
  errorInfo,
}: FallBackUIProps): JSX.Element => {
  const isDevelopment =
    process.env.NODE_ENV === "development" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  return (
    <div style={fallBackUiContainerStyle}>
      <div style={containerStyle}>
        <h2 style={headingStyle}>Oooopps! An Error occured.</h2>
        <p style={paragraphStyle}>
          The developers have been notified. Your issue is being investigated.
        </p>

        {isDevelopment && error && (
          <div
            style={{
              textAlign: "left",
              background: "#f8f8f8",
              padding: "20px",
              borderRadius: "8px",
              marginTop: "20px",
              marginBottom: "20px",
              overflow: "auto",
              maxWidth: "800px",
            }}
          >
            <h3 style={{ color: "red" }}>{error.toString()}</h3>
            <pre style={{ fontSize: "14px", whiteSpace: "pre-wrap" }}>
              {errorInfo?.componentStack}
            </pre>
          </div>
        )}

        <Button
          style={{
            fontWeight: "normal",
            fontFamily: "Titillium Web",
          }}
          onClick={refresh}
          className="base-button"
          size="large"
        >
          Click on this button to refresh
        </Button>
      </div>
    </div>
  );
};

export default class ErrorBoundary extends React.Component<
  Record<string, unknown>,
  State
> {
  constructor(props: Record<string, unknown>) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ error, errorInfo });
    Sentry.setExtra("stack", JSON.stringify(errorInfo.componentStack, null, 2));
    Sentry.captureException(error);
  }

  refresh = (): void => {
    window.location.reload();
  };

  render(): JSX.Element {
    if (this.state.hasError) {
      return (
        <FallBackUI
          refresh={this.refresh}
          error={this.state.error}
          errorInfo={this.state.errorInfo}
        />
      );
    }
    return <React.Fragment>{this.props.children}</React.Fragment>;
  }
}
